import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import OwnInnIcon from '@/components/icons/OwnInnIcon';
import { useTranslation } from 'react-i18next';
import HideIcon from '@/components/icons/HideIcon';
import ShowIcon from '@/components/icons/ShowIcon';
import ELoadingButton from '@/components/everridge/ELoadingButton.tsx';
import EToast from '@/components/everridge/EToast.tsx';
import { LoginResponseDto, TwoFactorAuthResponseDto } from '@/@types/auth.types.ts';
import { cn } from '@/lib/utils';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { healthCheckQueryKey } from '@/features/maintenance/healthCheckQuery';
import {
    hasMaintenanceLoginAccess,
    isLoginAllowedDuringMaintenance,
} from '@/features/maintenance/maintenanceStatusQuery';
import { useMaintenanceApi } from '@/features/maintenance/hooks/useMaintenanceApi';
import { ErrorKey, isUnavailableAuthError } from '@/config/apiErrorKeys.ts';
import { t } from 'i18next';
import { useAuthForm } from '@/features/identity/hooks/useAuthForm.ts';
import { useLoginApi } from '@/features/identity/hooks/useLoginApi.ts';
import { useSignIn } from '@/features/identity/hooks/useSignIn.ts';
import { AuthHelper } from '@/utils/auth.helper.ts';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { setMaintenanceRedirectState } from '@/features/maintenance/redirectState.ts';

const errorKeys = {
    emailNotConfirmed: 'ERR_EMAIL_NOT_CONFIRMED',
    emailNotFound: 'ERR_USER_NOT_FOUND',
    invalidPassword: 'ERR_INVALID_PASSWORD',
    maintenanceLoginNotAllowed: 'ERR_MAINTENANCE_LOGIN_NOT_ALLOWED',
};

const FormSchema = z.object({
    userName: z.string().email({
        message: t('auth.loginForm.form.email.error'),
    }),
    password: z.string().min(1, {
        message: t('auth.loginForm.form.password.error'),
    }),
    remember: z.boolean(),
});

type FormData = z.infer<typeof FormSchema>;

const LoginForm = () => {
    const { setLoginRequest, setTwoFactorToken, setEmail } = useAuthForm();
    const { t } = useTranslation();
    const signIn = useSignIn();
    const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const returnUrl = new URLSearchParams(location.search).get('returnUrl');
    const { maintenanceStatus, allowedLogins } = useMaintenanceApi();
    const maintenanceLoginAccess = hasMaintenanceLoginAccess(maintenanceStatus);

    const handleOnError = (errorKey: ErrorKey) => {
        if (isUnavailableAuthError(errorKey)) {
            queryClient.invalidateQueries({ queryKey: healthCheckQueryKey });
            setMaintenanceRedirectState(returnUrl);
            EToast({
                variant: 'error',
                message: t('auth.unavailable.message'),
                description: t('auth.unavailable.description'),
            });
            navigate('/maintenance', { replace: true });
            return;
        }

        EToast({
            variant: 'error',
            message: t('auth.loginForm.error.message'),
            description: t('auth.loginForm.error.description'),
        });
    };

    const { doLogin, isDoLoginWorking } = useLoginApi({
        onError: handleOnError,
        onSuccess: onLoginSuccess,
    });

    const [showPassword, setShowPassword] = useState<boolean>(false);

    const form = useForm<FormData>({
        resolver: zodResolver(FormSchema),
        defaultValues: {
            userName: '',
            password: '',
            remember: false,
        },
    });

    const { control, handleSubmit, getValues, setError } = form;

    const onSubmit = async (values: FormData) => {
        if (
            maintenanceLoginAccess &&
            !isLoginAllowedDuringMaintenance(values.userName, allowedLogins)
        ) {
            setError('userName', {
                message: t('auth.loginForm.maintenanceLoginNotAllowed'),
            });
            return;
        }

        setLoginRequest(values);
        await doLogin(values).catch((error) => {
            const errorData = error.response?.data;
            if (!errorData) {
                return;
            }

            const errorKey = errorData.code;
            const errorMessage = errorData.detail;

            if (errorKey === errorKeys.maintenanceLoginNotAllowed) {
                setError('userName', {
                    message: t('auth.loginForm.maintenanceLoginNotAllowed'),
                });
                return;
            }

            if (errorKey === errorKeys.emailNotConfirmed) {
                setError('userName', { message: `${errorMessage}` });
            }

            if (errorKey === errorKeys.emailNotFound) {
                setError('userName', { message: `${'User not found'}` });
            }

            if (errorKey === errorKeys.invalidPassword) {
                setError('password', { message: `${'Invalid password'}` });
            }
        });
    };

    const showPasswordHandler = () => {
        setShowPassword((prev) => !prev);
    };

    async function onLoginSuccess(result: LoginResponseDto | TwoFactorAuthResponseDto) {
        if ('twoFactorRequired' in result && result.token) {
            setTwoFactorToken(result.token);
            setEmail(getValues('userName'));
            const twoFactorPath = returnUrl
                ? `/auth/two-factor-login/?returnUrl=${encodeURIComponent(returnUrl)}`
                : '/auth/two-factor-login/';
            navigate(twoFactorPath);
            return;
        }

        const loginResponse = result as LoginResponseDto;

        // Legacy user authenticated with their old password but must set a new one to finish
        // migrating to Keycloak.
        if (loginResponse.passwordChangeRequired && loginResponse.migrationToken) {
            setEmail(getValues('userName'));
            navigate('/auth/new-password', {
                state: {
                    migrationToken: loginResponse.migrationToken,
                    email: getValues('userName'),
                },
            });
            return;
        }

        const claims = AuthHelper.parseJwtTokenClaims(loginResponse.tokens.accessToken);

        const user = AuthHelper.createUser(loginResponse.user, claims);

        if (!user) {
            EToast({
                variant: 'error',
                message: t('auth.loginForm.error.message'),
                description: t('auth.loginForm.error.description'),
            });
            return;
        }

        signIn({
            tokens: loginResponse.tokens,
            user,
        });

        navigate('/auth/choose-hotel', { replace: true });
    }

    const inputClasses =
        'h-11 p-3.5 border border-popover-100 bg-popover-10 text-xs text-popover-800 placeholder:text-xs placeholder:text-popover-600 active:shadow-input-active error:shadow-input-error';

    return (
        <Form {...form}>
            <div
                className={cn(
                    'my-auto flex-1 flex flex-col gap-6 rounded-2.5xl bg-popover-10',
                    isMobile ? 'grow' : 'max-w-[20.5rem]',
                )}
            >
                <div className="flex flex-col items-center gap-4">
                    <OwnInnIcon className="size-11" />
                    <h3 className="text-2xl font-semibold select-none">
                        {t('auth.loginForm.text.title')}
                    </h3>
                </div>
                {maintenanceLoginAccess && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                        {t('auth.loginForm.maintenanceBanner')}
                    </div>
                )}
                <div className="flex flex-col gap-8">
                    <div className="flex flex-col gap-4">
                        <FormField
                            control={control}
                            name="userName"
                            render={({ field }) => (
                                <FormItem className="flex flex-col gap-1.5">
                                    <FormLabel className="text-sm font-medium select-none">
                                        {t('elements.form.email.label')}
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            className={inputClasses}
                                            type="email"
                                            placeholder={t('your@email.com')}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={control}
                            name="password"
                            render={({ field }) => (
                                <FormItem className="flex flex-col gap-1.5">
                                    <FormLabel className="text-sm font-medium select-none">
                                        {t('elements.form.password.label')}
                                    </FormLabel>
                                    <FormControl>
                                        <div className="relative">
                                            <Input
                                                className={inputClasses}
                                                type={showPassword ? 'text' : 'password'}
                                                placeholder={t(
                                                    'auth.loginForm.form.password.placeholder',
                                                )}
                                                {...field}
                                            />
                                            <Button
                                                type="button"
                                                variant="link"
                                                className={
                                                    'absolute right-2.5 top-1/2 -translate-y-1/2 h-fit p-0'
                                                }
                                                onClick={showPasswordHandler}
                                            >
                                                {showPassword ? <ShowIcon /> : <HideIcon />}
                                            </Button>
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <div className="space-y-0 flex justify-end">
                            <Button
                                onClick={() => navigate('/auth/forgot-password')}
                                type="button"
                                variant="link"
                                className="p-0 hover:text-popover-800 active:text-popover-600"
                            >
                                {t('auth.loginForm.form.forgot')}
                            </Button>
                        </div>
                    </div>
                    <ELoadingButton
                        isLoading={isDoLoginWorking}
                        onClick={handleSubmit(onSubmit)}
                        text={t('elements.buttons.login')}
                    />
                    <div className="flex justify-evenly gap-4">
                        <Button
                            variant="link"
                            className="p-0 max-w-1/2 whitespace-normal no-underline"
                            onClick={() => navigate('/privacy-policy')}
                        >
                            {t('auth.loginForm.text.pricavy')}
                        </Button>
                        <Button
                            variant="link"
                            className="p-0 max-w-1/2 whitespace-normal no-underline"
                            onClick={() => navigate('/terms-of-service')}
                        >
                            {t('auth.loginForm.text.service')}
                        </Button>
                    </div>
                </div>
            </div>
        </Form>
    );
};

export default LoginForm;
